/**
 * AdaptiveTabs renders 'tabs' in a custom dropdown menu from
 * default to lg, and in horizontal tabs from lg and up.
 */
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import PropTypes from 'prop-types'
import R from 'ramda'
import styled from 'styled-components'
import useMedia from 'use-media'
import { NavLink, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'

import {
  Box,
  Icon,
  TransparentButton,
  Container,
  mediaQueries,
  shouldForwardProp,
} from '@redriverpress/system'

import Tabs, { getTabKey, TabsPropType } from './Tabs'

const MotionIcon = motion(Icon)

// Duplicated from @redriverpress/system because it made more sense than exporting this from there
const MenuItem = styled(TransparentButton)
  .withConfig({ shouldForwardProp })
  .attrs(props => ({
    borderRadius: 'rounded',
    color: 'black',
    display: 'block',
    fontSize: 0,
    fontWeight: 600,
    py: 2,
    px: ['0.75rem', null, 3], // 0.75rem on mobile so items in full width menus line up w/ site container
    textAlign: 'left',
    width: 1,
    as: props.as || (props.href ? 'a' : 'button'),
  }))`
  transition: unset; /* Override link global style */

  /* Radix manages focus programatically, so it's triggered on hover as well */
  &:focus {
    background: ${props =>
      props.disabled ? 'transparent' : props.theme.colors.blue500};
    color: ${props =>
      props.disabled ? 'currentColor' : props.theme.colors.white};
    outline: none;
  }
`

MenuItem.displayName = 'DropdownMenu.Item'

function NavLinkItem({ disabled, to, icon, label }) {
  return (
    <DropdownMenu.Item asChild disabled={disabled}>
      <MenuItem disabled={disabled} to={to} as={NavLink}>
        {icon && (
          <Box as='span' display='inline-block' mr={2} width={20}>
            <Icon icon={icon} textAlign='center' />
          </Box>
        )}
        {label}
      </MenuItem>
    </DropdownMenu.Item>
  )
}

NavLinkItem.defaultProps = {
  disabled: false,
  icon: null,
}

NavLinkItem.propTypes = {
  disabled: PropTypes.bool,
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  icon: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
}

NavLinkItem.displayName = 'DropdownMenu.NavLink'

const Dropdown = ({ tabs, DropdownChevron }) => {
  const { pathname } = useLocation()
  const [isOpen, setOpen] = useState(false)
  const [navBarHeight, setNavBarHeight] = useState(0)

  const tabsWithoutSearch = tabs.map(t => ({
    ...t,
    to: R.is(String, t.to) ? t.to.split('?')[0] : t.to,
  }))
  const currentTab =
    // Simple `to` prop
    R.find(R.propEq('to', pathname), tabsWithoutSearch) ||
    // Object `to` prop
    R.find(R.pathEq(['to', 'pathname'], pathname), tabsWithoutSearch)

  useEffect(() => {
    // Close menu when path changes
    setOpen(false)
  }, [pathname])

  useEffect(() => {
    const navbarElement = document.querySelector('[data-esll-navbar]')
    setNavBarHeight(navbarElement.offsetHeight)
  }, [])

  return (
    <DropdownMenu.Root onOpenChange={o => setOpen(o)}>
      <DropdownMenu.Trigger asChild>
        <TransparentButton
          position='sticky'
          top={navBarHeight}
          zIndex={1}
          bg='white'
          boxShadow={2}
          py={3}
          px={0}
          textAlign='left'
          width={1}
        >
          {/* 'active' className set in case tab labels use the NavLink .active class for styling */}
          <Container
            display='flex'
            alignItems='center'
            fontWeight='600'
            color='black'
            className='active'
          >
            {currentTab?.icon && (
              <Box as='span' mr={2} width={20}>
                <Icon icon={currentTab.icon} />
              </Box>
            )}
            {currentTab?.label}
            <DropdownChevron isOpen={isOpen} />
          </Container>
        </TransparentButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content asChild align='center' sideOffset={4}>
        <Box
          bg='white'
          width='100vw'
          boxShadow='inset 0px 4px 4px rgba(0, 0, 0, 0.1)'
          p={1}
          position='relative'
          mt='-5px'
        >
          {tabs.map(tab => (
            <NavLinkItem
              as={NavLink}
              exact={tab.exact}
              to={tab.to}
              key={getTabKey(tab)}
              label={tab.label}
              icon={tab.icon}
            />
          ))}
        </Box>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}

Dropdown.propTypes = {
  tabs: TabsPropType.isRequired,
  DropdownChevron: PropTypes.func.isRequired,
}

export default function AdaptiveTabs({
  tabs,
  DropdownChevron,
  tabsBreakpoint,
}) {
  const isTabsBreakpoint = useMedia(tabsBreakpoint)

  return isTabsBreakpoint ? (
    <Tabs tabs={tabs} />
  ) : (
    <Dropdown tabs={tabs} DropdownChevron={DropdownChevron} />
  )
}

export const DefaultChevron = ({ isOpen, ...props }) => (
  <MotionIcon
    icon='chevron-down'
    ml='auto'
    animate={{ rotate: isOpen ? 180 : 0 }}
    {...props}
  />
)
DefaultChevron.propTypes = {
  isOpen: PropTypes.bool.isRequired,
}

AdaptiveTabs.defaultProps = {
  tabsBreakpoint: mediaQueries.lg,
  DropdownChevron: DefaultChevron,
}

AdaptiveTabs.propTypes = {
  tabs: TabsPropType.isRequired,
  tabsBreakpoint: PropTypes.oneOf(
    Object.keys(mediaQueries).map(k => mediaQueries[k])
  ),
  DropdownChevron: PropTypes.func,
}
